import {
  EmptyState,
  FlexBox,
  IconButton,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { SavedSearch } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';

import { searchApi } from 'apis/search';
import { appActions } from 'app/app.actions';
import { searchActions } from 'app/views/layout/views/search/search.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizeHtml } from 'utils/sanitizeHtml';

import { SavedSearchFilters } from '../filters/savedSearchFilters';

import styles from './savedSearchListModal.module.scss';

export const SavedSearchListModal = createAppModal(
  { type: 'search:savedSearch:list' },
  ({ isOpen, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();

    const [selected, setSelected] = useState<SavedSearch>();

    const activeSavedSearch = useAppSelector(
      (state) => state.search.savedSearch,
    );

    const { savedSearches, isLoading } = searchApi.useReadSavedSearchesQuery(
      undefined,
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          savedSearches: data?.data.savedSearches ?? [],
        }),
      },
    );

    const [deleteSavedSearch] = searchApi.useDeleteSavedSearchMutation();

    const handleApply = () => {
      if (!selected) return;

      ampli.savedSearchApplied();
      dispatch(searchActions.savedSearch.apply(selected));

      notify(
        {
          title: 'Saved search applied',
          text: sanitizeHtml(
            `"${selected.name}" has been applied.`,
            'plainText',
          ),
          className: 'success',
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      hide?.();
    };

    const handleDelete = async (savedSearch: SavedSearch) => {
      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: `Delete "${savedSearch.name}"`,
              description: 'Are you sure you want to delete this saved search?',
              confirm: {
                label: 'Delete',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        try {
          await deleteSavedSearch({ id: savedSearch.id });

          notify(
            {
              title: 'Saved search deleted',
              text: sanitizeHtml(
                `"${savedSearch.name}" has been deleted.`,
                'plainText',
              ),
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );

          if (selected?.id === savedSearch.id) {
            setSelected(undefined);
          }

          ampli.savedSearchDeleted();

          return Promise.resolve({ isDone: true });
        } catch (error) {
          setSelected(undefined);

          notify(
            {
              title: 'Error while deleting the saved search.',
            },
            NotificationType.Error,
          );
        }
      }
    };

    const renderContent = () => {
      const getCardClassName = (savedSearch: SavedSearch) =>
        classnames({
          [styles.listCard]: true,
          [styles.listCardSelected]: selected?.id === savedSearch.id,
          [styles.listCardDisabled]: activeSavedSearch?.id === savedSearch.id,
        });

      if (isLoading) {
        return (
          <FlexBox justifyContent="center">
            <Spinner size={32} />
          </FlexBox>
        );
      }

      if (!savedSearches.length) {
        return (
          <EmptyState
            title="You haven't saved any searches yet"
            description="Saving a search to come back to later is as easy as searching using a keyword or some filters and giving it a name."
            buttonProps={{
              children: 'Go back',
              onClick: hide,
            }}
          />
        );
      }

      return (
        <div className={styles.list}>
          {savedSearches.map((savedSearch) => {
            const isActive = activeSavedSearch?.id === savedSearch.id;

            return (
              <FlexBox
                key={savedSearch.id}
                className={getCardClassName(savedSearch)}
                onClick={() => setSelected(savedSearch)}
              >
                <div>
                  <Text.Heading>{savedSearch.name}</Text.Heading>

                  <SavedSearchFilters
                    variant="text"
                    params={savedSearch.searchRequest}
                  />
                </div>

                {!isActive && (
                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    onClick={() => handleDelete(savedSearch)}
                  />
                )}
              </FlexBox>
            );
          })}
        </div>
      );
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Saved searches" onClose={hide}>
          Here are the searches that you’ve saved. Select one to use it again.
        </ModalHead>

        <ModalBody>{renderContent()}</ModalBody>

        {savedSearches.length > 0 && (
          <ModalFoot
            confirmName="Apply selected search"
            cancelName="Cancel"
            confirmOnClick={handleApply}
            cancelOnClick={hide}
            confirmProps={{
              disabled: !selected,
            }}
          />
        )}
      </Modal>
    );
  },
);
